import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, LogIn } from "lucide-react";
import { API, formatApiError, setToken } from "@/lib/api";
import { Field } from "@/components/onboarding/Field";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setToken(data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#08090E] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="xy-eyebrow mb-2">a division of</p>
          <p className="font-display text-sm font-semibold uppercase tracking-[0.35em] text-slate-200">x | a</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500 mb-3">content + mgmt + tech</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="admin-login-title">
            XYION HQ
          </h1>
        </div>
        <form onSubmit={onSubmit} className="xy-card p-8 space-y-5" data-testid="admin-login-form">
          <div className="flex items-center gap-2 text-slate-400">
            <Lock size={14} />
            <p className="text-xs font-mono uppercase tracking-[0.2em]">Restricted access</p>
          </div>
          <Field
            label="Email"
            testid="input-admin-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Field
            label="Password"
            testid="input-admin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error ? (
            <p className="text-sm text-red-400" data-testid="admin-login-error">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            data-testid="btn-admin-login"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(139,92,246,0.35)] transition-all hover:bg-violet-500 disabled:opacity-40"
          >
            <LogIn size={16} /> {loading ? "Signing in..." : "Sign in"}
          </button>
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <button
            type="button"
            data-testid="btn-google-signin"
            onClick={signInWithGoogle}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-sm font-semibold text-slate-200 transition-all hover:border-white/30 hover:bg-white/[0.08]"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
              <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"/>
              <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z"/>
            </svg>
            Continue with Google
          </button>
        </form>
        <p className="mt-6 text-center">
          <Link to="/" data-testid="link-back-to-form" className="text-xs text-slate-600 hover:text-violet-300 transition-colors">
            Back to client onboarding
          </Link>
        </p>
      </div>
    </div>
  );
}
